import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { morgansClient } from '../../../lib/connect/clients';
const versionsKey = [
    'qualification',
    'versions'
];
const evaluationKey = (opportunityId)=>[
        'qualification',
        'evaluation',
        opportunityId
    ];
const eventsKey = (evaluationId)=>[
        'qualification',
        'events',
        evaluationId
    ];
export function parseCriteria(criteriaJson) {
    try {
        const parsed = JSON.parse(criteriaJson || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.flatMap((c)=>{
            const o = c;
            if (!o || typeof o.id !== 'string' || !o.id) return [];
            return [
                {
                    id: o.id,
                    label: typeof o.label === 'string' && o.label ? o.label : o.id,
                    ...typeof o.weight === 'number' && o.weight > 0 ? {
                        weight: o.weight
                    } : {}
                }
            ];
        });
    } catch  {
        return [];
    }
}
export function parseAnswers(answersJson) {
    try {
        const parsed = JSON.parse(answersJson || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return parsed;
    } catch  {
        return {};
    }
}
export function displayAnswer(value) {
    if (value == null) return '';
    return typeof value === 'string' ? value : JSON.stringify(value);
}
/** Every published template version of the tenant (SAD-474). */ export function useQualificationVersions() {
    return useQuery({
        queryKey: versionsKey,
        queryFn: async ()=>(await morgansClient.listQualificationTemplateVersions({})).versions
    });
}
/** The Opportunity's live evaluation, else its latest; null when it has none. */ export function useQualificationEvaluation(opportunityId) {
    return useQuery({
        queryKey: evaluationKey(opportunityId),
        queryFn: async ()=>(await morgansClient.getQualificationEvaluation({
                opportunityId
            })).evaluation ?? null
    });
}
/** Defer/resume basis events of an evaluation, oldest first. */ export function useQualificationEvents(evaluationId) {
    return useQuery({
        queryKey: eventsKey(evaluationId ?? ''),
        enabled: !!evaluationId,
        queryFn: async ()=>(await morgansClient.listQualificationEvaluationEvents({
                evaluationId: evaluationId ?? ''
            })).events
    });
}
/** Start (idempotent), save answers, defer, resume and disposition one Opportunity's evaluation. */ export function useQualificationMutations(opportunityId) {
    const queryClient = useQueryClient();
    const refresh = ()=>{
        void queryClient.invalidateQueries({
            queryKey: [
                'qualification',
                'evaluation',
                opportunityId
            ]
        });
        void queryClient.invalidateQueries({
            queryKey: [
                'qualification',
                'events'
            ]
        });
    };
    const start = useMutation({
        mutationFn: ()=>morgansClient.startQualificationEvaluation({
                opportunityId
            }),
        onSettled: refresh
    });
    const saveAnswers = useMutation({
        mutationFn: (v)=>morgansClient.updateQualificationAnswers({
                evaluationId: v.evaluationId,
                answersJson: JSON.stringify(v.answers)
            }),
        onSettled: refresh
    });
    const refreshSources = useMutation({
        mutationFn: ()=>morgansClient.getQualificationEvaluation({
                opportunityId,
                refreshSuggestions: true
            }),
        onSuccess: (r)=>queryClient.setQueryData(evaluationKey(opportunityId), r.evaluation ?? null)
    });
    const review = useMutation({
        mutationFn: (v)=>morgansClient.updateQualificationAnswers(v),
        onSettled: refresh
    });
    const defer = useMutation({
        mutationFn: (evaluationId)=>morgansClient.deferQualificationEvaluation({
                evaluationId
            }),
        onSettled: refresh
    });
    const resume = useMutation({
        mutationFn: (evaluationId)=>morgansClient.resumeQualificationEvaluation({
                evaluationId
            }),
        onSettled: refresh
    });
    const disposition = useMutation({
        mutationFn: (v)=>morgansClient.dispositionQualificationEvaluation(v),
        onSettled: refresh
    });
    return {
        start,
        saveAnswers,
        defer,
        resume,
        disposition,
        refreshSources,
        review
    };
}
export function parseSuggestions(value) {
    try {
        const parsed = JSON.parse(value || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch  {
        return [];
    }
}
export function parseBasis(value) {
    try {
        return JSON.parse(value || '{}');
    } catch  {
        return {};
    }
}
